import { createContext, useContext, useEffect, useState } from "react";
import { MsalProvider } from "@azure/msal-react";
import { loginRequest } from "../auth-config";
import { msalInstance, msalInitPromise } from "../msal-instance";
import { User } from "../../models/User";
import { UserType } from "../../models/UserType";
import { LanguageCode } from "../../models/enums/LanguageCode";

interface AuthContextType {
	user: User;
	isAuthenticated: boolean;
	isLoading: boolean;
	setUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
	const [user, setUser] = useState<User>({
		id: 0,
		externalId: "",
		email: "",
		firstName: "",
		lastName: "",
		type: UserType.Internal,
		companyId: undefined,
		createdAt: new Date().toISOString(),
		updatedAt: undefined,
		company: undefined
	});

	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	// Function to decode JWT token
	const parseJwt = (token: string): any => {
		try {
			const base64Url = token.split(".")[1];
			const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
			const jsonPayload = decodeURIComponent(
				atob(base64)
					.split("")
					.map(function (c) {
						return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
					})
					.join("")
			);
			return JSON.parse(jsonPayload);
		} catch (e) {
			return {};
		}
	};

	useEffect(() => {
		const initializeAuth = async () => {
			try {
				setIsLoading(true);

				// Wait for MSAL to be initialized (shared instance)
				await msalInitPromise;

				const response = await msalInstance.handleRedirectPromise();

				if (response && response.account) {
					msalInstance.setActiveAccount(response.account);
				}

				const account = msalInstance.getActiveAccount();

				if (account) {
					try {
						// Get access token to extract roles and user information
						const tokenResponse = await msalInstance.acquireTokenSilent({
							account: account,
							scopes: loginRequest.scopes
						});

						// Decode access token
						const decodedAccessToken = parseJwt(tokenResponse.accessToken);

						// Extract user information from access token based on known properties
						const email = decodedAccessToken.preferred_username || "";

						// Try to get first name from given_name first, then fall back to name or FirstName
						const firstName =
							decodedAccessToken.given_name ||
							decodedAccessToken.FirstName ||
							decodedAccessToken.name ||
							"";

						// Try to get last name from family_name, LastName (with capital L), or from splitting name
						const lastName =
							decodedAccessToken.family_name ||
							decodedAccessToken.LastName ||
							"";

						const externalId =
							decodedAccessToken.ExternalId || decodedAccessToken.oid || "";
						const profil = decodedAccessToken.Profil || "";

						// Extract roles - handle various formats
						let roles: string[] = [];
						if (Array.isArray(decodedAccessToken.roles)) {
							roles = decodedAccessToken.roles;
						} else if (typeof decodedAccessToken.roles === "string") {
							roles = [decodedAccessToken.roles];
						}

						// Determine user type based on profile
						let type: UserType = UserType.Internal;
						if (profil === "Freelance") {
							type = UserType.Freelancer;
						} else if (profil === "Entreprise") {
							type = UserType.Client;
						} else if (roles.some(role => ["admin", "tam", "csm"].includes(role))) {
							type = UserType.Internal;
						}

						// Update user state with extracted information
						const userObject = {
							id: 0,
							externalId,
							email,
							firstName,
							lastName,
							type,
							companyId: undefined,
							createdAt: new Date().toISOString(),
							updatedAt: undefined,
							company: undefined,
							roles,
							settings: {
								id: 0,
								preferredLanguage:
									navigator.language.split("-")[0] === LanguageCode.Fr.toLowerCase()
										? LanguageCode.Fr
										: LanguageCode.En
							}
						};

						setUser(userObject);

						setIsAuthenticated(true);

						// Redirect to saved URL if exists (after login from a deep link)
						const returnUrl = sessionStorage.getItem("returnUrl");
						if (returnUrl && returnUrl !== "/" && returnUrl !== "/login") {
							sessionStorage.removeItem("returnUrl");
							window.location.href = returnUrl;
						}
					} catch (error) {
						console.error("Error acquiring token:", error);
						// Don't force a redirect here
					}
				} else {
					// Check if there are any accounts we could use
					const accounts = msalInstance.getAllAccounts();
					if (accounts.length > 0) {
						msalInstance.setActiveAccount(accounts[0]);
						// Refresh page to let the effect run again with the active account
						window.location.reload();
						return;
					}
				}
			} catch (error) {
			} finally {
				setIsLoading(false);
			}
		};

		initializeAuth();
	}, []);

	return (
		<AuthContext.Provider value={{ user, isAuthenticated, isLoading, setUser }}>
			<MsalProvider instance={msalInstance}>{children}</MsalProvider>
		</AuthContext.Provider>
	);
};

export const useAuthProvider = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuthProvider must be used within an AuthProvider");
	}
	return context;
};
